import { useCallback, useEffect, useState } from "react";
import type { WindowWithGoogle } from "@components/GoogleIdentity/GoogleButton/contract";
import { ERROR_ORIGIN } from "clientState/GoogleIdentityProvider/GoogleTrackingErrorProvider/TrackingErrorComponent";
import { useIsNative } from "@edreamsodigeo/frontend-core";

import type { CallbackType, UseGoogleSignInSetup } from "./contract";
import { GOT_ID } from "./GoogleOneTap/constants";
import { GOOGLE_SRC } from "./googleScript";
import { getInitializer } from "./initializeCallbackHandler";
import { NONE } from "./stateOptions";

enum GoogleScriptState {
  NOT_LOADED,
  LOADED,
  READY,
}
const defaultStatus = GoogleScriptState.NOT_LOADED;
export const googleLoginSuccessTokenDefault = null;

const isScriptInDOM = (): boolean =>
  document.querySelector(`script[src="${GOOGLE_SRC}"]`) !== null;

const isGoogleLoaded = (): boolean => window?.google?.accounts !== undefined;

export const useGoogleSignInSetup = ({ appId }): UseGoogleSignInSetup => {
  const [googleScriptState, setGoogleScriptState] =
    useState<GoogleScriptState>(defaultStatus);
  const [googleLoginSuccessToken, setGoogleLoginSuccessToken] = useState<
    string | null
  >(googleLoginSuccessTokenDefault);
  const [state, setState] = useState<string>(NONE);
  const isNative = useIsNative();

  (window as unknown as WindowWithGoogle).onGoogleLibraryLoad = (): void => {
    setGoogleScriptState(GoogleScriptState.LOADED);
  };

  const callback: CallbackType = (data) => {
    const state = getInitializer(data);
    setGoogleLoginSuccessToken(data.credential);
    setState(state || NONE);
  };

  useEffect(() => {
    if (isNative || (!isGoogleLoaded() && isScriptInDOM())) {
      // Need this to avoid download multiples scripts
      return;
    }
    if (!isGoogleLoaded()) {
      const script = document.createElement("script");
      script.src = GOOGLE_SRC;
      script.async = true;
      document.body.appendChild(script);
    } else if (googleScriptState === GoogleScriptState.NOT_LOADED) {
      setGoogleScriptState(GoogleScriptState.LOADED);
    }
    if (googleScriptState === GoogleScriptState.LOADED) {
      try {
        window.google.accounts.id.initialize({
          auto_select: false,
          callback,
          cancel_on_tap_outside: false,
          client_id: appId,
          use_fedcm_for_prompt: true,
          prompt_parent_id: GOT_ID,
        });
        setGoogleScriptState(GoogleScriptState.READY);
      } catch {
        window.document.getElementById(ERROR_ORIGIN.initialize)?.click();
        throw new Error("Error initializing Google");
      }
    }
  }, [appId, googleScriptState, isNative]);

  const resetGoogleSignIn = useCallback(() => {
    setGoogleLoginSuccessToken(googleLoginSuccessTokenDefault);
    setState(NONE);
  }, []);

  return {
    isGoogleScriptLoaded: googleScriptState === GoogleScriptState.READY,
    googleLoginSuccessToken,
    state,
    resetGoogleSignIn,
  };
};
